import { useState, useEffect } from 'react'
import api from '../../services/api'
import toast from 'react-hot-toast'
import { useTheme } from '../../context/ThemeContext'
import {
  Package,
  Search,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Camera,
  Video,
  Mic,
  Lamp,
  Monitor,
  Plus,
  X,
  FileText,
  ShieldCheck,
  RotateCcw
} from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'

export default function StudentEquipment() {
  const { darkMode } = useTheme()
  const [activeTab, setActiveTab] = useState('catalog') // catalog | requests | custody | history
  const [loading, setLoading] = useState(true)
  const [equipmentList, setEquipmentList] = useState([])
  const [myRequests, setMyRequests] = useState([])
  const [studentProfileData, setStudentProfileData] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterCategory, setFilterCategory] = useState('All')
  
  // Request Modal State
  const [showRequestModal, setShowRequestModal] = useState(false)
  const [showGuidelinesModal, setShowGuidelinesModal] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [courses, setCourses] = useState([])
  const [submittingRequest, setSubmittingRequest] = useState(false)
  const [requestForm, setRequestForm] = useState({
    equipment_id: '',
    quantity_requested: 1,
    requested_start_at: '',
    requested_return_at: '',
    purpose: '',
    course_id: '',
    activity_name: '',
    notes: '',
    guidelines_acknowledged: false
  })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      // 1. Fetch catalog
      const eqRes = await api.get('/equipment')
      setEquipmentList(eqRes.data || [])

      // 2. Fetch my requests
      const reqRes = await api.get('/equipment/requests/my-requests')
      setMyRequests(reqRes.data || [])

      // 3. Fetch profile to get student ID, then load custody & history
      const profileRes = await api.get('/students/profile')
      if (profileRes.data?.id) {
        const studentEqRes = await api.get(`/equipment/student/${profileRes.data.id}`)
        setStudentProfileData(studentEqRes.data || null)
      }

      // 4. Load courses for optional context
      const coursesRes = await api.get('/students/dashboard')
      if (coursesRes.data?.enrollments) {
        setCourses(coursesRes.data.enrollments)
      }

    } catch (err) {
      console.error('Error loading equipment data:', err)
      toast.error('Failed to load equipment information')
    } finally {
      setLoading(false)
    }
  }

  const handleOpenRequest = (item) => {
    setSelectedItem(item)
    // Default dates: tomorrow 9am to day-after-tomorrow 5pm
    const tomorrow = new Date(Date.now() + 86400000)
    tomorrow.setHours(9, 0, 0, 0)
    const returnDate = new Date(Date.now() + 86400000 * 2)
    returnDate.setHours(17, 0, 0, 0)

    const formatDt = (d) => {
      const pad = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    }

    setRequestForm({
      equipment_id: item.id,
      quantity_requested: 1,
      requested_start_at: formatDt(tomorrow),
      requested_return_at: formatDt(returnDate),
      purpose: '',
      course_id: '',
      activity_name: '',
      notes: '',
      guidelines_acknowledged: false
    })
    setShowRequestModal(true)
  }

  const handleCloseRequest = () => {
    setShowRequestModal(false)
    setSelectedItem(null)
  }

  const handleSubmitRequest = async (e) => {
    e.preventDefault()
    if (!requestForm.guidelines_acknowledged) {
      toast.error('You must read and agree to the Equipment Use & Care Guidelines before submitting your request.')
      return
    }

    setSubmittingRequest(true)
    try {
      await api.post('/equipment/requests', {
        ...requestForm,
        quantity_requested: parseInt(requestForm.quantity_requested, 10),
        course_id: requestForm.course_id ? parseInt(requestForm.course_id, 10) : null,
        guidelines_acknowledged: true,
        guidelines_version: 'v1.0-2026'
      })
      toast.success('Equipment request submitted! Awaiting administrator approval.')
      handleCloseRequest()
      setActiveTab('requests')
      loadData()
    } catch (err) {
      console.error('Submit request failed:', err)
      toast.error(err.response?.data?.error || 'Failed to submit equipment request')
    } finally {
      setSubmittingRequest(false)
    }
  }

  const handleCancelRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this equipment request?')) return
    try {
      await api.post(`/equipment/requests/${requestId}/cancel`)
      toast.success('Equipment request cancelled')
      loadData()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to cancel request')
    }
  }

  const getCategoryIcon = (category) => {
    const icons = {
      'Camera': Camera,
      'Stabilizer': Video,
      'Microphone': Mic,
      'Lighting': Lamp,
      'Computer': Monitor,
      'Graphics Tablet': Monitor,
      'Audio Recorder': Mic
    }
    const Icon = icons[category] || Package
    return <Icon className="w-5 h-5" />
  }

  const filteredEquipment = equipmentList.filter(item => {
    const matchesSearch = 
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.serial_number?.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesCat = filterCategory === 'All' || item.category === filterCategory
    return matchesSearch && matchesCat
  })

  const categories = ['All', ...new Set(equipmentList.map(e => e.category).filter(Boolean))]

  const currentlyCheckedOut = studentProfileData?.currently_checked_out || []
  const pastHistory = studentProfileData?.history || []

  if (loading) return <LoadingSpinner label="Loading equipment portal" />

  return (
    <div className={`space-y-8 ${darkMode ? '' : 'admin-light'}`}>
      {/* Header */}
      <div className="cinema-page-header">
        <div>
          <span className="cinema-label block mb-1">Student Portal</span>
          <h1 className="font-display text-2xl sm:text-3xl text-gray-900 dark:text-white font-light">
            Equipment & Studio Gear
          </h1>
          <p className="text-xs text-gray-500 dark:text-cinema-muted mt-1">
            Browse available academy equipment, submit booking requests, and track your custody & returns.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-white/10 space-x-2 sm:space-x-4 overflow-x-auto">
        <button
          onClick={() => setActiveTab('catalog')}
          className={`py-3 px-4 text-sm font-sans font-medium transition-all flex items-center gap-2 border-b-2 whitespace-nowrap ${
            activeTab === 'catalog'
              ? 'border-primary-500 dark:border-cinema-glow text-primary-600 dark:text-cinema-glow font-semibold'
              : 'border-transparent text-gray-500 dark:text-cinema-muted hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          <Package className="w-4 h-4" />
          Equipment Catalog
          <span className="px-1.5 py-0.2 text-[10px] bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-white rounded-none">{equipmentList.length}</span>
        </button>

        <button
          onClick={() => setActiveTab('requests')}
          className={`py-3 px-4 text-sm font-sans font-medium transition-all flex items-center gap-2 border-b-2 whitespace-nowrap ${
            activeTab === 'requests'
              ? 'border-primary-500 dark:border-cinema-glow text-primary-600 dark:text-cinema-glow font-semibold'
              : 'border-transparent text-gray-500 dark:text-cinema-muted hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          <Clock className="w-4 h-4" />
          My Requests
          {myRequests.filter(r => ['pending', 'approved', 'ready_for_collection'].includes(r.status)).length > 0 && (
            <span className="px-1.5 py-0.2 text-[10px] bg-amber-500/10 dark:bg-amber-500/20 text-amber-600 dark:text-amber-300 rounded-none font-bold">
              {myRequests.filter(r => ['pending', 'approved', 'ready_for_collection'].includes(r.status)).length}
            </span>
          )}
        </button>

        <button
          onClick={() => setActiveTab('custody')}
          className={`py-3 px-4 text-sm font-sans font-medium transition-all flex items-center gap-2 border-b-2 whitespace-nowrap ${
            activeTab === 'custody'
              ? 'border-primary-500 dark:border-cinema-glow text-primary-600 dark:text-cinema-glow font-semibold'
              : 'border-transparent text-gray-500 dark:text-cinema-muted hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          <ShieldCheck className="w-4 h-4" />
          Currently In Custody
          {currentlyCheckedOut.length > 0 && (
            <span className="px-1.5 py-0.2 text-[10px] bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-300 rounded-none font-bold">
              {currentlyCheckedOut.length}
            </span>
          )}
        </button>

        <button
          onClick={() => setActiveTab('history')}
          className={`py-3 px-4 text-sm font-sans font-medium transition-all flex items-center gap-2 border-b-2 whitespace-nowrap ${
            activeTab === 'history'
              ? 'border-primary-500 dark:border-cinema-glow text-primary-600 dark:text-cinema-glow font-semibold'
              : 'border-transparent text-gray-500 dark:text-cinema-muted hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          <RotateCcw className="w-4 h-4" />
          Past Returns & History
          <span className="px-1.5 py-0.2 text-[10px] bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-white rounded-none">{pastHistory.length}</span>
        </button>
      </div>

      {/* ─────────────────────────────────────────────────────────────────────────────
          TAB 1: CATALOG
      ───────────────────────────────────────────────────────────────────────────── */}
      {activeTab === 'catalog' && (
        <div className="space-y-6">
          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="relative sm:col-span-2">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-cinema-muted w-4 h-4" />
              <input
                type="text"
                placeholder="Search equipment by name, category, serial number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-white dark:bg-white/5 border border-gray-300 dark:border-white/10 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary-500 dark:focus:border-cinema-glow/50 rounded-none placeholder:text-gray-400 dark:placeholder:text-cinema-muted/40"
              />
            </div>

            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-2 bg-white dark:bg-white/5 border border-gray-300 dark:border-white/10 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary-500 dark:focus:border-cinema-glow/50 rounded-none"
            >
              {categories.map(c => (
                <option key={c} value={c} className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white">{c}</option>
              ))}
            </select>
          </div>

          {/* Cardless Structured Equipment List */}
          <div className="divide-y divide-gray-200 dark:divide-white/10 border-t border-b border-gray-200 dark:border-white/10">
            {filteredEquipment.map((item) => {
              const isAvailable = item.available_quantity > 0
              return (
                <div key={item.id} className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors">
                  <div className="flex items-start gap-3.5 min-w-0">
                    <div className="p-2.5 rounded-none bg-gray-100 dark:bg-white/5 text-primary-600 dark:text-cinema-glow shrink-0 mt-0.5">
                      {getCategoryIcon(item.category)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900 dark:text-white text-base leading-snug">{item.name}</h3>
                        <span className={`px-2 py-0.2 text-[11px] rounded-none font-medium ${
                          isAvailable
                            ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                            : 'bg-red-500/10 text-red-600 dark:text-red-400'
                        }`}>
                          {isAvailable ? `${item.available_quantity} Available` : 'Unavailable'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-cinema-muted font-mono mt-0.5">
                        {item.category} • Code: {item.equipment_code} {item.serial_number && `• S/N: ${item.serial_number}`} {item.location && `• Location: ${item.location}`}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-cinema-soft line-clamp-1 mt-1">
                        {item.description || 'Professional academy production equipment.'}
                      </p>
                      {item.accessories && (
                        <p className="text-xs text-gray-500 dark:text-cinema-muted/80 mt-0.5">
                          <span className="text-primary-600 dark:text-cinema-glow font-medium">Includes:</span> {item.accessories}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-6 shrink-0 sm:self-center">
                    <div className="text-right text-xs text-gray-500 dark:text-cinema-muted hidden md:block">
                      <span className="block font-mono text-gray-900 dark:text-white">{item.total_quantity} total units</span>
                      <span className="block text-[11px] text-gray-500 dark:text-cinema-soft">{item.in_use_quantity} in custody • {item.reserved_quantity} reserved</span>
                    </div>
                    <button
                      onClick={() => handleOpenRequest(item)}
                      disabled={!isAvailable}
                      className={`py-2 px-4 text-xs font-medium rounded-none flex items-center gap-1.5 transition-all ${
                        isAvailable
                          ? 'cinema-btn-primary'
                          : 'bg-gray-100 dark:bg-white/5 text-gray-400 dark:text-cinema-muted cursor-not-allowed border border-gray-200 dark:border-white/5'
                      }`}
                    >
                      <Plus className="w-3.5 h-3.5" />
                      {isAvailable ? 'Request Item' : 'Fully Booked'}
                    </button>
                  </div>
                </div>
              )
            })}
          </div>

          {filteredEquipment.length === 0 && (
            <div className="text-center py-16 text-cinema-muted">
              <Package className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-base font-medium">No equipment matching search criteria</p>
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────────────────
          TAB 2: MY REQUESTS
      ───────────────────────────────────────────────────────────────────────────── */}
      {activeTab === 'requests' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center pb-2 border-b border-white/10">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-cinema-muted">My Equipment Requests</h2>
            <button
              onClick={() => setActiveTab('catalog')}
              className="text-xs text-cinema-glow hover:underline flex items-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" /> Request New Item
            </button>
          </div>

          {myRequests.length === 0 ? (
            <div className="text-center py-16 text-cinema-muted">
              <Clock className="w-10 h-10 mx-auto text-cinema-muted/40 mb-3" />
              <p className="text-sm text-white font-medium">No equipment requests submitted yet</p>
              <p className="text-xs text-cinema-muted mt-1">Browse the catalog to request gear for your practical projects.</p>
            </div>
          ) : (
            <div className="divide-y divide-white/10 border-t border-b border-white/10">
              {myRequests.map((req) => {
                const isPending = req.status === 'pending'
                const isApproved = req.status === 'approved'
                const isReady = req.status === 'ready_for_collection'
                const isCollected = req.status === 'collected'
                const isDeclined = req.status === 'declined'
                const isCancelled = req.status === 'cancelled'

                return (
                  <div key={req.id} className="py-4 space-y-3 hover:bg-white/[0.01] transition-colors">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-cinema-muted">{req.request_code}</span>
                          <span className={`px-2 py-0.5 text-[11px] rounded font-semibold uppercase tracking-wider ${
                            isPending ? 'bg-amber-500/10 text-amber-400' :
                            isApproved ? 'bg-blue-500/10 text-blue-400' :
                            isReady ? 'bg-emerald-500/20 text-emerald-300 font-bold' :
                            isCollected ? 'bg-purple-500/10 text-purple-400' :
                            isDeclined ? 'bg-red-500/10 text-red-400' :
                            'bg-gray-500/10 text-gray-400'
                          }`}>
                            {req.status.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <h3 className="text-base font-semibold text-white mt-1">{req.equipment_name}</h3>
                      </div>

                      <div className="text-right">
                        <span className="text-xs text-cinema-muted">Quantity: </span>
                        <span className="text-sm font-semibold text-white font-mono">
                          {req.approved_quantity || req.quantity_requested} unit(s)
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 text-xs text-cinema-muted">
                      <div>
                        <span className="text-white block font-medium">Requested Period:</span>
                        <span>{new Date(req.requested_start_at).toLocaleDateString()} — {new Date(req.requested_return_at).toLocaleDateString()}</span>
                      </div>
                      <div>
                        <span className="text-white block font-medium">Purpose:</span>
                        <span className="line-clamp-2">{req.purpose}</span>
                      </div>
                      <div>
                        <span className="text-white block font-medium">Submitted:</span>
                        <span>{new Date(req.requested_at).toLocaleDateString()}</span>
                      </div>
                    </div>

                    {isReady && (
                      <div className="p-3 bg-emerald-500/10 border-l-2 border-emerald-500 flex items-center justify-between">
                        <div className="flex items-center gap-2 text-emerald-300 text-xs">
                          <CheckCircle className="w-4 h-4 shrink-0" />
                          <span>Your equipment is prepared and ready at the Equipment Desk. Please collect during lab hours.</span>
                        </div>
                      </div>
                    )}

                    {isDeclined && req.decline_reason && (
                      <div className="p-3 bg-red-500/10 border-l-2 border-red-500 text-xs text-red-300">
                        <span className="font-semibold">Reason for decline:</span> {req.decline_reason}
                      </div>
                    )}

                    {req.approval_notes && (
                      <div className="p-2.5 bg-white/5 border-l-2 border-blue-500 text-xs text-cinema-muted">
                        <span className="text-white font-medium">Staff notes:</span> {req.approval_notes}
                      </div>
                    )}

                    <div className="flex flex-wrap items-center justify-between text-xs text-cinema-muted pt-1 gap-2">
                      <div className="flex items-center gap-1.5 text-emerald-400 font-medium">
                        <ShieldCheck className="w-3.5 h-3.5" />
                        <span>Policy Acknowledged ({req.guidelines_version || 'v1.0-2026'})</span>
                      </div>

                      {['pending', 'approved'].includes(req.status) && (
                        <button
                          onClick={() => handleCancelRequest(req.id)}
                          className="text-xs text-red-400 hover:text-red-300 transition-colors flex items-center gap-1"
                        >
                          <X className="w-3.5 h-3.5" /> Cancel Request
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────────────────
          TAB 3: CURRENTLY IN CUSTODY
      ───────────────────────────────────────────────────────────────────────────── */}
      {activeTab === 'custody' && (
        <div className="space-y-4">
          <div className="pb-2 border-b border-white/10">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-cinema-muted">Active Equipment In Your Custody</h2>
          </div>

          {currentlyCheckedOut.length === 0 ? (
            <div className="text-center py-16 text-cinema-muted">
              <ShieldCheck className="w-10 h-10 mx-auto text-cinema-muted/40 mb-3" />
              <p className="text-sm text-white font-medium">No equipment currently checked out</p>
              <p className="text-xs text-cinema-muted mt-1">You do not currently have any equipment in your possession.</p>
            </div>
          ) : (
            <div className="divide-y divide-white/10 border-t border-b border-white/10">
              {currentlyCheckedOut.map((item) => {
                const isOverdue = item.is_overdue
                const dueDate = new Date(item.expected_return_date)
                const now = new Date()
                const daysDiff = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24))

                return (
                  <div key={item.id} className="py-4 space-y-3 hover:bg-white/[0.01] transition-colors">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-cinema-muted">{item.issue_code}</span>
                          <span className={`px-2 py-0.5 text-[11px] rounded font-semibold uppercase tracking-wider ${
                            isOverdue
                              ? 'bg-red-500/20 text-red-300 font-bold'
                              : 'bg-emerald-500/10 text-emerald-400'
                          }`}>
                            {isOverdue ? 'Overdue' : 'Checked Out'}
                          </span>
                        </div>
                        <h3 className="text-base font-semibold text-white mt-1">{item.equipment_name}</h3>
                      </div>

                      <div className="text-right">
                        <span className="text-xs text-cinema-muted">Outstanding: </span>
                        <span className="text-sm font-semibold text-white font-mono">{item.outstanding_quantity} / {item.quantity} unit(s)</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                      <div>
                        <span className="text-cinema-muted block">Issued At:</span>
                        <span className="text-white">{new Date(item.issued_at).toLocaleDateString()}</span>
                      </div>
                      <div>
                        <span className="text-cinema-muted block">Expected Return Date:</span>
                        <span className={`font-semibold ${isOverdue ? 'text-red-400' : 'text-white'}`}>
                          {dueDate.toLocaleDateString()} {isOverdue ? `(${Math.abs(daysDiff)} days overdue!)` : `(${daysDiff} days left)`}
                        </span>
                      </div>
                      <div>
                        <span className="text-cinema-muted block">Condition At Issue:</span>
                        <span className="text-white capitalize">{item.condition_at_issue}</span>
                      </div>
                    </div>

                    {item.accessories_issued && (
                      <div className="p-2.5 bg-white/5 border-l-2 border-cinema-glow text-xs text-cinema-muted">
                        <span className="text-white font-medium">Accessories in custody:</span> {item.accessories_issued}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────────────────
          TAB 4: PAST HISTORY & RETURN RECORDS
      ───────────────────────────────────────────────────────────────────────────── */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          <div className="pb-2 border-b border-white/10">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-cinema-muted">Past Equipment Checkouts & Returns</h2>
          </div>

          {pastHistory.length === 0 ? (
            <div className="text-center py-16 text-cinema-muted">
              <RotateCcw className="w-10 h-10 mx-auto text-cinema-muted/40 mb-3" />
              <p className="text-sm text-white font-medium">No past equipment history recorded</p>
            </div>
          ) : (
            <div className="divide-y divide-white/10 border-t border-b border-white/10">
              {pastHistory.map((item) => (
                <div key={item.id} className="py-4 space-y-2 hover:bg-white/[0.01] transition-colors">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-mono text-cinema-muted">{item.issue_code}</span>
                    <span className={`px-2 py-0.5 rounded font-semibold uppercase tracking-wider text-[10px] ${
                      item.status === 'returned' ? 'bg-emerald-500/10 text-emerald-400' :
                      item.status === 'damaged' ? 'bg-amber-500/10 text-amber-400' :
                      item.status === 'lost' ? 'bg-red-500/10 text-red-400' :
                      'bg-white/5 text-cinema-muted'
                    }`}>
                      {item.status}
                    </span>
                  </div>

                  <div className="flex justify-between items-baseline">
                    <h3 className="text-sm font-semibold text-white">{item.equipment_name}</h3>
                    <span className="text-xs text-cinema-muted font-mono">{item.quantity} unit(s)</span>
                  </div>

                  <div className="text-xs text-cinema-muted flex flex-wrap gap-4 pt-1">
                    <span>Issued: {new Date(item.issued_at).toLocaleDateString()}</span>
                    <span>Staff: {item.issued_by_first_name} {item.issued_by_last_name}</span>
                    <span>Purpose: {item.purpose}</span>
                  </div>

                  {Array.isArray(item.return_events) && item.return_events.length > 0 && (
                    <div className="mt-2 space-y-1.5 pt-2 border-t border-white/5">
                      <span className="text-[11px] font-semibold text-cinema-muted block">Return Events:</span>
                      {item.return_events.map((ev, idx) => (
                        <div key={idx} className="p-2 bg-white/5 text-[11px] flex justify-between items-center">
                          <div>
                            <span className="text-white font-medium">
                              Returned {ev.quantity_returned} Good, {ev.quantity_damaged} Damaged, {ev.quantity_lost} Lost
                            </span>
                            <span className="text-cinema-muted block">{ev.return_notes || ev.damage_details || 'No notes'}</span>
                          </div>
                          <span className="text-cinema-muted font-mono">{new Date(ev.returned_at).toLocaleDateString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────────────────
          MODAL: SUBMIT EQUIPMENT REQUEST
      ───────────────────────────────────────────────────────────────────────────── */}
      {showRequestModal && selectedItem && (
        <div className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#12131a] border border-gray-200 dark:border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-5 rounded-none text-xs text-gray-900 dark:text-white">
            <div className="flex items-start justify-between border-b border-gray-200 dark:border-white/10 pb-4">
              <div>
                <span className="text-[10px] font-mono uppercase tracking-widest text-primary-600 dark:text-cinema-glow font-bold">
                  Synergy Multimedia Academy • Equipment Request
                </span>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {selectedItem.name}
                </h2>
                <span className="text-xs text-gray-500 dark:text-cinema-muted font-mono">{selectedItem.category} • Available: {selectedItem.available_quantity}</span>
              </div>
              <button onClick={handleCloseRequest} className="text-gray-400 hover:text-gray-700 dark:hover:text-white p-1 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmitRequest} className="space-y-4 text-xs">
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-wider text-gray-700 dark:text-gray-300 mb-1">
                  Quantity Required * (Max {selectedItem.available_quantity})
                </label>
                <input
                  type="number"
                  min="1"
                  max={selectedItem.available_quantity}
                  required
                  value={requestForm.quantity_requested}
                  onChange={(e) => setRequestForm({ ...requestForm, quantity_requested: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white text-xs outline-none focus:border-primary-500 dark:focus:border-cinema-glow rounded-none"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-mono uppercase tracking-wider text-gray-700 dark:text-gray-300 mb-1">
                    Start Date & Time *
                  </label>
                  <input
                    type="datetime-local"
                    required
                    value={requestForm.requested_start_at}
                    onChange={(e) => setRequestForm({ ...requestForm, requested_start_at: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white text-xs outline-none focus:border-primary-500 dark:focus:border-cinema-glow rounded-none"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-mono uppercase tracking-wider text-gray-700 dark:text-gray-300 mb-1">
                    Expected Return Date & Time *
                  </label>
                  <input
                    type="datetime-local"
                    required
                    value={requestForm.requested_return_at}
                    onChange={(e) => setRequestForm({ ...requestForm, requested_return_at: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white text-xs outline-none focus:border-primary-500 dark:focus:border-cinema-glow rounded-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-mono uppercase tracking-wider text-gray-700 dark:text-gray-300 mb-1">
                  Project / Purpose *
                </label>
                <textarea
                  rows="2"
                  required
                  placeholder="Describe what practical session or project you need this equipment for..."
                  value={requestForm.purpose}
                  onChange={(e) => setRequestForm({ ...requestForm, purpose: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white text-xs outline-none focus:border-primary-500 dark:focus:border-cinema-glow placeholder:text-gray-400 dark:placeholder:text-cinema-muted/40 rounded-none"
                />
              </div>

              {courses.length > 0 && (
                <div>
                  <label className="block text-[11px] font-mono uppercase tracking-wider text-gray-700 dark:text-gray-300 mb-1">
                    Associated Course (Optional)
                  </label>
                  <select
                    value={requestForm.course_id}
                    onChange={(e) => setRequestForm({ ...requestForm, course_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 bg-white dark:bg-[#1a1b24] text-gray-900 dark:text-white text-xs outline-none focus:border-primary-500 dark:focus:border-cinema-glow rounded-none"
                  >
                    <option value="" className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white">None / General Practical</option>
                    {courses.map(c => (
                      <option key={c.course_id} value={c.course_id} className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
                        {c.title} ({c.course_code})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-[11px] font-mono uppercase tracking-wider text-gray-700 dark:text-gray-300 mb-1">
                  Additional Notes / Specific Accessories Requested
                </label>
                <input
                  type="text"
                  placeholder="e.g. Need extra battery, tripod, or SD card..."
                  value={requestForm.notes}
                  onChange={(e) => setRequestForm({ ...requestForm, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white text-xs outline-none focus:border-primary-500 dark:focus:border-cinema-glow placeholder:text-gray-400 dark:placeholder:text-cinema-muted/40 rounded-none"
                />
              </div>

              {/* Mandatory Guideline Acknowledgement Section */}
              <div className="pt-3 border-t border-gray-200 dark:border-white/10 space-y-3">
                <div className="space-y-1">
                  <div className="text-[11px] font-mono uppercase tracking-wider text-primary-600 dark:text-cinema-glow font-bold">
                    BEFORE YOU REQUEST EQUIPMENT
                  </div>
                  <p className="text-xs text-gray-600 dark:text-cinema-muted leading-relaxed">
                    Please read the SMA Equipment Use & Care Guidelines before submitting this request. These guidelines explain your responsibilities while using academy equipment, including care, accessories, returns, damage, loss, overdue equipment and consequences of misuse.
                  </p>
                </div>

                <div>
                  <button
                    type="button"
                    onClick={() => setShowGuidelinesModal(true)}
                    className="w-full py-2.5 px-3 border border-gray-300 dark:border-white/20 bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 text-gray-900 dark:text-white text-xs font-semibold tracking-wide uppercase flex items-center justify-center gap-2 transition-colors rounded-none"
                  >
                    <ShieldCheck className="w-4 h-4 text-primary-600 dark:text-cinema-glow" />
                    <span>READ EQUIPMENT USE & CARE GUIDELINES</span>
                  </button>
                </div>

                <div className="pt-1">
                  <label className="flex items-start gap-2.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      required
                      checked={requestForm.guidelines_acknowledged}
                      onChange={(e) => setRequestForm({ ...requestForm, guidelines_acknowledged: e.target.checked })}
                      className="mt-0.5 rounded-none border-gray-300 dark:border-white/30 bg-white dark:bg-black/40 text-primary-600 dark:text-cinema-glow focus:ring-0 focus:outline-none"
                    />
                    <span className="text-xs text-gray-900 dark:text-white leading-snug">
                      I have read and agree to the Equipment Use & Care Guidelines.
                    </span>
                  </label>
                  <div className="text-[10px] text-gray-500 dark:text-cinema-muted/60 pl-6 pt-0.5">
                    Policy Version: 1.0 (2026) • Synergy Multimedia Academy
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-white/10">
                <button
                  type="button"
                  onClick={handleCloseRequest}
                  className="flex-1 py-2 px-4 text-xs font-medium border border-gray-300 dark:border-white/20 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors rounded-none"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingRequest || !requestForm.guidelines_acknowledged}
                  className="flex-1 cinema-btn-primary py-2 text-xs font-medium flex items-center justify-center gap-1.5 disabled:opacity-50 rounded-none"
                >
                  {submittingRequest ? <LoadingSpinner size="sm" inline /> : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────────────────
          MODAL: FULL EQUIPMENT USE & CARE GUIDELINES
      ───────────────────────────────────────────────────────────────────────────── */}
      {showGuidelinesModal && (
        <div className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-xs flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-[#12131a] border border-gray-200 dark:border-white/15 w-full max-w-2xl max-h-[85vh] flex flex-col p-6 space-y-4 rounded-none text-gray-900 dark:text-white">
            <div className="flex items-start justify-between border-b border-gray-200 dark:border-white/10 pb-3">
              <div>
                <span className="text-xs font-mono text-gray-500 dark:text-cinema-muted uppercase tracking-wider">Synergy Multimedia Academy</span>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white mt-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>EQUIPMENT USE & CARE GUIDELINES</h2>
                <span className="text-xs text-primary-600 dark:text-cinema-glow font-mono font-medium">Official Policy • Version 1.0 (2026)</span>
              </div>
              <button
                onClick={() => setShowGuidelinesModal(false)}
                className="text-gray-400 hover:text-gray-700 dark:hover:text-white p-1 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="overflow-y-auto space-y-4 text-xs text-gray-700 dark:text-cinema-muted leading-relaxed pr-2">
              <section className="space-y-1.5">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">A. Before Collecting Equipment</h3>
                <p>Students must:</p>
                <ul className="list-disc pl-5 space-y-1 text-gray-600 dark:text-gray-300">
                  <li>Request equipment through the student portal where applicable.</li>
                  <li>Provide accurate information about intended use, projects, and activities.</li>
                  <li>Only request equipment they are authorized or reasonably qualified to use.</li>
                  <li>Wait for official approval before attempting to collect equipment.</li>
                  <li>Inspect equipment and all accessories at physical collection with staff.</li>
                  <li>Report existing damage, missing accessories, or operational defects BEFORE leaving the equipment desk.</li>
                </ul>
                <p className="text-amber-600 dark:text-amber-400/90 font-medium">
                  Notice: If existing damage or missing items are not reported before collection, the student may be considered to have accepted the recorded condition.
                </p>
              </section>

              <section className="space-y-1.5">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">B. While Using Equipment</h3>
                <p>Students must:</p>
                <ul className="list-disc pl-5 space-y-1 text-gray-600 dark:text-gray-300">
                  <li>Use equipment only for the approved educational/project purpose.</li>
                  <li>Follow academy staff and instructor instructions at all times.</li>
                  <li>Handle equipment carefully, professionally, and respectfully.</li>
                  <li>Use protective cases, lens caps, straps, stands, bags and provided safety accessories.</li>
                  <li>Protect equipment from avoidable water, dust, extreme heat, unsafe environments and hazards.</li>
                  <li>Never perform unauthorized repairs, modifications, or disassembly.</li>
                  <li>Never lend, transfer, or hand academy equipment to another person without authorization.</li>
                  <li>Keep equipment under direct supervision and take reasonable steps to prevent theft or loss.</li>
                </ul>
              </section>

              <section className="space-y-1.5">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">C. Accessories Responsibility</h3>
                <p>Students are responsible for all accessories issued with equipment, including:</p>
                <ul className="list-disc pl-5 space-y-1 text-gray-600 dark:text-gray-300">
                  <li>Batteries, battery chargers, power cables, and AC adapters.</li>
                  <li>Memory cards (SD/CFast), card readers, and USB cables.</li>
                  <li>Protective bags, hard cases, straps, and lens hoods.</li>
                  <li>Tripods, quick-release plates, mounts, and stands.</li>
                  <li>Microphones, windscreens, XLR cables, and transmitters.</li>
                </ul>
                <p className="text-gray-900 dark:text-white font-medium">Everything issued must be returned in full.</p>
              </section>

              <section className="space-y-1.5">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">D. Returning Equipment</h3>
                <p>Students must:</p>
                <ul className="list-disc pl-5 space-y-1 text-gray-600 dark:text-gray-300">
                  <li>Return equipment strictly by the approved date and time.</li>
                  <li>Return it directly to an authorized academy staff member at the equipment desk.</li>
                  <li>Return all issued accessories neatly packed in their designated cases.</li>
                  <li>Return equipment in a reasonably clean and maintained condition.</li>
                  <li>Never leave equipment unattended or hand it to another student to return on their behalf.</li>
                </ul>
                <p className="text-gray-900 dark:text-white font-medium">Equipment remains the student's legal responsibility until officially checked in and signed off by staff.</p>
              </section>

              <section className="space-y-1.5">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">E. Damage Reporting & Inspection</h3>
                <p>
                  Students must report damage immediately. Students must never hide damage or attempt unauthorized repairs. The academy inspects equipment to determine: what happened, whether it was accidental or negligent, whether it was used appropriately, and whether repair is possible. Accidental damage reported honestly is treated with understanding.
                </p>
              </section>

              <section className="space-y-1.5">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">F. Loss or Theft</h3>
                <p>
                  If equipment is lost or stolen, notify academy administration immediately with all relevant details. The academy will review circumstances before determining required actions.
                </p>
              </section>

              <section className="space-y-1.5">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">G. Handling Standards & Consequences</h3>
                <p>Poor handling may result in consequences depending on severity and circumstances:</p>
                <ul className="list-disc pl-5 space-y-1 text-gray-600 dark:text-gray-300">
                  <li><strong className="text-gray-900 dark:text-white">Minor / First-Time:</strong> Verbal/written warning, care retraining, or temporary restriction on sensitive gear.</li>
                  <li><strong className="text-gray-900 dark:text-white">Repeated Negligence:</strong> Suspension of borrowing privileges or supervised-only equipment usage.</li>
                  <li><strong className="text-gray-900 dark:text-white">Serious Negligence / Misuse / Loss:</strong> Disciplinary action and, where appropriate, responsibility for reasonable repair or replacement costs.</li>
                </ul>
              </section>

              <section className="space-y-1.5">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">H. Overdue Equipment</h3>
                <p>
                  Equipment returned past the deadline is flagged as Overdue. Future requests are restricted until overdue gear is returned. If an extension is needed, request it before the deadline; extensions are only valid once explicitly approved.
                </p>
              </section>

              <section className="space-y-1.5">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">I. No Unauthorized Transfers</h3>
                <p>
                  Students may never transfer equipment to another student directly. Every borrower must request and check out through the official system.
                </p>
              </section>
            </div>

            <div className="pt-3 border-t border-gray-200 dark:border-white/10 flex justify-between items-center">
              <span className="text-[11px] text-gray-500 dark:text-cinema-muted">Synergy Multimedia Academy • Student Equipment Policy</span>
              <button
                type="button"
                onClick={() => {
                  setShowGuidelinesModal(false)
                  setRequestForm(prev => ({ ...prev, guidelines_acknowledged: true }))
                }}
                className="cinema-btn-primary py-2 px-5 text-xs font-medium rounded-none"
              >
                I Understand & Agree
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
